import { useState } from 'react';
import { ChevronDown, ChevronUp, Navigation, Waves, Loader2, Droplets, Thermometer, Info, Fish, BookOpen, Lock, Smartphone } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { getConditionColor } from '@/utils/mapConstants';

// Inline "Get the app" CTA shown under the free preview in gated tabs.
export function ProUpgradeInline({ title, subtitle, onUpgrade, testId }) {
  return (
    <div
      className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 text-center"
      data-testid={testId || 'pro-upgrade-inline'}
    >
      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
        <Lock className="w-4 h-4 text-amber-600" />
      </div>
      <h4 className="text-sm font-bold text-amber-900 mb-0.5">{title}</h4>
      {subtitle && <p className="text-xs text-amber-700 mb-3 leading-relaxed">{subtitle}</p>}
      <Button
        size="sm"
        onClick={onUpgrade}
        className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
        data-testid={testId ? `${testId}-btn` : 'pro-upgrade-inline-btn'}
      >
        <Smartphone className="w-3.5 h-3.5 mr-1.5" />
        Get the app
      </Button>
    </div>
  );
}

const POI_CONFIG = {
  fishing_access: { label: 'Fishing Access', color: '#16a34a', icon: '/icons/poi/fishing-access@2x.png' },
  boat_ramp: { label: 'Boat Ramps', color: '#2563eb', icon: '/icons/poi/boat-ramp@2x.png' },
  stream_gauge: { label: 'Flow Stations', color: '#7c3aed', icon: '/icons/poi/stream-gauge@2x.png' },
  rapids: { label: 'Rapids', color: '#dc2626', icon: '/icons/poi/rapids@2x.png' },
  dam: { label: 'Dams', color: '#64748b', icon: '/icons/poi/dam@2x.png' },
  waterfall: { label: 'Waterfalls', color: '#0891b2', icon: '/icons/poi/waterfall@2x.png' },
  bridge: { label: 'Bridges', color: '#78716c', icon: '/icons/poi/bridge@2x.png' },
  hazard: { label: 'Hazards', color: '#ef4444', icon: '/icons/poi/hazard@2x.png' },
  fly_shop: { label: 'Fly Shops', color: '#ca8a04', icon: '/icons/poi/fly-shop@2x.png' },
  lodging: { label: 'Lodging', color: '#9333ea', icon: '/icons/poi/lodging@2x.png' },
  campsite: { label: 'Campsites', color: '#15803d', icon: '/icons/poi/campsite@2x.png' },
  trailhead: { label: 'Trailheads', color: '#b45309', icon: '/icons/poi/trailhead@2x.png' },
  parking: { label: 'Parking', color: '#6366f1', icon: '/icons/poi/parking@2x.png' },
  restroom: { label: 'Restrooms', color: '#0d9488', icon: '/icons/poi/restroom@2x.png' },
  other: { label: 'Other', color: '#6b7280', icon: '/icons/poi/other@2x.png' },
};

export function formatDistance(meters) {
  if (meters < 100) return `${Math.round(meters)}m`;
  if (meters < 1609) return `${(meters / 1000).toFixed(1)}km`;
  return `${(meters / 1609.34).toFixed(1)}mi`;
}

function splitOverview(text) {
  if (!text) return { summary: '', recommendations: '' };
  const trimmed = text.trim();
  const hasHeadings = /^##\s/m.test(trimmed);
  if (hasHeadings) {
    const idx = trimmed.search(/^##\s/m);
    const before = trimmed.slice(0, idx).trim();
    if (before) return { summary: before, recommendations: trimmed.slice(idx).trim() };
    const match = trimmed.match(/^##[^\n]*\n+([^#*\n][^\n]+(?:\n[^#*\n][^\n]+)*)/m);
    return { summary: match ? match[1].trim() : '', recommendations: trimmed };
  }
  if (trimmed.length < 500 && !trimmed.includes('**') && !trimmed.includes('* '))
    return { summary: trimmed, recommendations: '' };
  if (trimmed.includes('**') || trimmed.includes('* '))
    return { summary: '', recommendations: trimmed };
  return { summary: trimmed, recommendations: '' };
}

export function OverviewTab({ river, onUpgrade }) {
  const bestSeasons = river.best_seasons || [];
  const species = river.target_species || river.species || [];
  const overview = river.detailed_description || river.description || '';
  const isGated = river.pro_required === true;
  if (!bestSeasons.length && !species.length && !overview && !isGated) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Waves className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Overview data not available for this waterway yet.</p>
      </div>
    );
  }
  const { summary, recommendations } = splitOverview(overview);
  // When gated, backend nulls `detailed_description` — so if we have any
  // overview text at all, it's the short summary/description. Show that as
  // the teaser and follow with an upgrade CTA for the full AI overview.
  return (
    <div className="space-y-4" data-testid="overview-tab-content">
      {bestSeasons.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {bestSeasons.map(s => <span key={s} className="text-xs font-semibold px-3 py-1 rounded-full border border-green-200 text-green-700 bg-green-50">{s}</span>)}
        </div>
      )}
      {species.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-2.5 flex items-center gap-2"><Fish className="w-4 h-4 text-green-600" />Target Species</h4>
          <div className="flex flex-wrap gap-2">
            {species.map(s => <span key={s} className="text-xs font-medium px-3 py-1 rounded-full border border-green-200 text-green-700 bg-white flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />{s}</span>)}
          </div>
        </div>
      )}
      {summary && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-blue-500" />About This Water</h4>
          <p className="text-sm text-gray-600 leading-relaxed">{summary}</p>
        </div>
      )}
      {recommendations && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-green-600" />Fishing Recommendations</h4>
          <div className="prose-river" data-testid="overview-recommendations">
            <ReactMarkdown components={{
              h1: ({ children }) => <h3 className="text-base font-bold text-gray-800 mt-4 mb-2 first:mt-0">{children}</h3>,
              h2: ({ children }) => <h3 className="text-base font-bold text-gray-800 mt-4 mb-2 first:mt-0">{children}</h3>,
              h3: ({ children }) => <h4 className="text-sm font-bold text-gray-700 mt-3 mb-1.5">{children}</h4>,
              p: ({ children }) => <p className="text-sm text-gray-600 leading-relaxed mb-2.5 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="space-y-2 mb-3 last:mb-0">{children}</ul>,
              ol: ({ children }) => <ol className="space-y-2 mb-3 last:mb-0 list-decimal list-inside">{children}</ol>,
              li: ({ children }) => <li className="text-sm text-gray-600 leading-relaxed pl-1">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
              em: ({ children }) => <em className="italic text-gray-500">{children}</em>,
              blockquote: ({ children }) => <blockquote className="border-l-3 border-green-300 pl-3 my-2 text-sm text-gray-500 italic">{children}</blockquote>,
              hr: () => <hr className="border-gray-200 my-3" />,
            }}>{recommendations}</ReactMarkdown>
          </div>
        </div>
      )}
      {isGated && (
        <ProUpgradeInline
          title="Unlock the full waterway overview"
          subtitle="The DIY Fly Fishing app has the complete fishing report, all seasons, hatch charts and detailed recommendations."
          onUpgrade={onUpgrade}
          testId="overview-pro-upgrade"
        />
      )}
    </div>
  );
}

export function ConditionsTab({ gaugeData, gaugeLoading, expandedChart, chartData, onToggleChart }) {
  if (gaugeLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /><span className="ml-2 text-sm text-gray-500">Loading flow stations...</span></div>;
  if (gaugeData.length === 0) return <div className="text-center py-8 text-gray-400"><Droplets className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No flow stations available for this waterway.</p></div>;
  return (
    <div className="space-y-3" data-testid="conditions-tab-content">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5 text-blue-500" /> Flow Stations</h4>
      {gaugeData.map(gauge => {
        const condColor = getConditionColor(gauge.condition);
        const isExpanded = expandedChart === gauge.site_id;
        const trends = chartData[gauge.site_id];
        let updatedAgo = '';
        if (gauge.timestamp) {
          const mins = Math.round((Date.now() - new Date(gauge.timestamp).getTime()) / 60000);
          if (mins < 60) updatedAgo = `Updated ${mins} min ago`;
          else if (mins < 1440) updatedAgo = `Updated ${Math.round(mins / 60)} hr ago`;
          else updatedAgo = `Updated ${Math.round(mins / 1440)} days ago`;
        }
        return (
          <div key={gauge.site_id} className="border border-gray-100 rounded-xl p-4" data-testid={`gauge-card-${gauge.site_id}`}>
            <div className="flex items-start justify-between mb-1">
              <div><p className="text-sm font-semibold text-gray-900">{gauge.site_name}</p>{updatedAgo && <p className="text-xs text-gray-400">{updatedAgo}</p>}</div>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: condColor + '20', color: condColor }}>{gauge.condition_label || gauge.condition || 'Unknown'}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {gauge.gage_height_ft != null && <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-xs text-gray-500 mb-0.5">Height</p><p className="text-base font-bold text-gray-800">{gauge.gage_height_ft}<span className="text-xs font-normal ml-0.5">FT</span></p></div>}
              {gauge.discharge_cfs != null && <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-xs text-gray-500 mb-0.5">Flow</p><p className="text-base font-bold text-gray-800">{gauge.discharge_cfs}<span className="text-xs font-normal ml-0.5">CFS</span></p></div>}
              {gauge.water_temp_f != null && <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-xs text-gray-500 mb-0.5 flex items-center justify-center gap-0.5"><Thermometer className="w-3 h-3" /> Temp</p><p className="text-base font-bold text-gray-800">{gauge.water_temp_f}<span className="text-xs font-normal ml-0.5">&deg;F</span></p></div>}
            </div>
            <button onClick={() => onToggleChart(gauge.site_id)} className="w-full mt-3 text-center text-xs font-medium text-teal-600 hover:text-teal-700 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors" data-testid={`toggle-chart-${gauge.site_id}`}>
              {isExpanded ? 'Hide Chart' : 'Show Chart'} {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {isExpanded && trends && !trends.error && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                {trends.sparkline_data?.length > 1 ? <MiniChart data={trends.sparkline_data} /> : <p className="text-xs text-gray-400 text-center py-2">No chart data available</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function HatchesTab({ hatchChart, proRequired, onUpgrade }) {
  const [seasonFilter, setSeasonFilter] = useState('All');
  const seasons = ['All', 'Spring', 'Summer', 'Fall', 'Winter'];
  // Empty-state: only show "not available" copy when there's genuinely no
  // data. For gated free users the backend returns a 1-item teaser, so we
  // fall through to the render path and let the CTA drive conversion.
  if ((!hatchChart || hatchChart.length === 0) && !proRequired) {
    return <div className="text-center py-8 text-gray-400"><Waves className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">Hatch data not available for this waterway yet.</p></div>;
  }
  // Canonical schema (matches mobile app + AI output): bug_name, hook_sizes,
  // matching_flies, lowercase importance. Fallbacks keep older docs rendering
  // until the one-time migration finishes.
  const importanceColorMap = { major: 'bg-green-600 text-white', moderate: 'bg-orange-500 text-white', minor: 'bg-gray-400 text-white' };
  const importanceBorderMap = { major: '#16a34a', moderate: '#f97316', minor: '#9ca3af' };
  const filteredHatches = seasonFilter === 'All' ? (hatchChart || []) : (hatchChart || []).filter(h => (h.seasons || h.months || h.peak_months || []).some(s => s.toLowerCase().includes(seasonFilter.toLowerCase())));
  return (
    <div className="space-y-3" data-testid="hatches-tab-content">
      <h4 className="text-sm font-bold text-gray-800">Bug Hatches & Matching Flies</h4>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {seasons.map(s => <button key={s} onClick={() => setSeasonFilter(s)} className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${seasonFilter === s ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-testid={`season-filter-${s.toLowerCase()}`}>{s}</button>)}
      </div>
      {filteredHatches.length === 0 && !proRequired ? <p className="text-sm text-gray-400 text-center py-4">No hatches for this season.</p> : (
        <div className="space-y-3">
          {filteredHatches.map((hatch, idx) => {
            const name = hatch.bug_name || hatch.common_name || hatch.name;
            const hookSize = hatch.hook_sizes || hatch.hook_size;
            const flies = hatch.matching_flies || hatch.recommended_flies || [];
            const importance = (hatch.importance || '').toLowerCase();
            return (
              <div key={name || idx} className="border border-gray-100 rounded-xl p-4 border-l-4" style={{ borderLeftColor: importanceBorderMap[importance] || '#e5e7eb' }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{name}</p>
                    {hatch.scientific_name && <p className="text-xs text-gray-400 italic">{hatch.scientific_name}</p>}
                    {hatch.type && <p className="text-xs text-gray-500 mt-0.5 capitalize">{hatch.type}</p>}
                  </div>
                  {importance && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${importanceColorMap[importance] || 'bg-gray-200 text-gray-600'}`}>{importance}</span>}
                </div>
                {hatch.description && <p className="text-sm text-gray-600 mt-2 leading-relaxed">{hatch.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  {hatch.time_of_day && <span className="capitalize">{hatch.time_of_day}</span>}
                  {hookSize && <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">#{hookSize}</span>}
                </div>
                {flies.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2.5">{flies.map(fly => <span key={fly} className="text-xs font-medium px-2.5 py-1 rounded-full border border-green-200 text-green-700 bg-green-50">{fly}</span>)}</div>}
              </div>
            );
          })}
        </div>
      )}
      {proRequired && (
        <ProUpgradeInline
          title="See every hatch on this water"
          subtitle="The full seasonal hatch chart with matching flies, hook sizes and time-of-day tips is in the DIY Fly Fishing app."
          onUpgrade={onUpgrade}
          testId="hatches-pro-upgrade"
        />
      )}
    </div>
  );
}

export function POIsTab({ sortedTypes, totalPoiCount, hasCorridor, expandedType, setExpandedType, onPoiClick }) {
  return (
    <div data-testid="pois-tab-content">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Points of Interest</h3>
        {totalPoiCount > 0 && <span className="text-xs font-bold bg-orange-500 text-white rounded-full px-2 py-0.5">{totalPoiCount}</span>}
      </div>
      {sortedTypes.length > 0 ? (
        <div className="space-y-1.5">
          {sortedTypes.map(([type, pois]) => {
            const config = POI_CONFIG[type] || POI_CONFIG.other;
            const isExpanded = expandedType === type;
            return (
              <div key={type} data-testid={`poi-category-${type}`}>
                <button onClick={() => setExpandedType(prev => prev === type ? null : type)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left" data-testid={`poi-category-btn-${type}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: config.color + '18' }}>
                    <img src={config.icon} alt={config.label} className="w-5 h-5" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <span className="flex-1 text-sm font-medium text-gray-800">{config.label}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: config.color + '18', color: config.color }}>{pois.length}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {isExpanded && (
                  <div className="ml-3 pl-3 border-l-2 mt-1 mb-2 space-y-0.5" style={{ borderColor: config.color + '40' }}>
                    {pois.slice(0, 50).map(poi => (
                      <button key={poi.id} onClick={() => onPoiClick?.(poi)} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-gray-50 transition-colors text-left group" data-testid={`poi-item-${poi.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate group-hover:text-gray-900">{poi.name || 'Unnamed'}</p>
                          <p className="text-xs text-gray-400">{formatDistance(poi.distance_m)} from river{poi.state && ` \u2022 ${poi.state}`}</p>
                        </div>
                        <Navigation className="w-3.5 h-3.5 text-gray-300 group-hover:text-teal-500 flex-shrink-0 transition-colors" />
                      </button>
                    ))}
                    {pois.length > 50 && <p className="text-xs text-gray-400 px-2.5 py-1">+ {pois.length - 50} more</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-400" data-testid="no-pois-message">
          <Waves className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">{hasCorridor ? 'No points of interest found along this waterway' : 'Corridor data not available for this waterway'}</p>
        </div>
      )}
    </div>
  );
}

export function MiniChart({ data, width = 280, height = 60 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.value ?? d);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => { const x = (i / (values.length - 1)) * width; const y = height - ((v - min) / range) * (height - 8) - 4; return `${x},${y}`; }).join(' L ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <path d={`M ${points}`} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={width} cy={height - ((values[values.length - 1] - min) / range) * (height - 8) - 4} r="3" fill="#3b82f6" />
    </svg>
  );
}
